import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import DashboardLayout from "../components/DashboardLayout";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import {
  Layers, Workflow, Network, Inbox, ArrowLeft, X, Plus, ShieldCheck,
  Check, ChevronDown, ChevronUp, RefreshCw, Eye, Archive,
} from "lucide-react";

/**
 * /dashboard/rbac/advanced
 * ----------------------------------------------------------------------
 * RBAC Phase C — Custom Roles + Approval Chains + Approval Inbox + Visual Canvas.
 *
 * Single tabbed admin surface for super_admin / org_admin.
 */
const TABS = [
  { key: "custom-roles", label: "Custom Roles", icon: Layers },
  { key: "chains", label: "Approval Chains", icon: Workflow },
  { key: "inbox", label: "Approval Inbox", icon: Inbox },
  { key: "canvas", label: "Visual Canvas", icon: Network },
];

export default function RBACPhaseCPage() {
  const [active, setActive] = useState("custom-roles");

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto" data-testid="rbac-phase-c-page">
        <Link to="/dashboard"
              className="text-xs text-slate-500 hover:text-[#0F4C81] inline-flex items-center gap-1">
          <ArrowLeft className="h-3 w-3" /> Back to dashboard
        </Link>
        <div className="mt-3 mb-6">
          <p className="text-[10px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold">
            Advanced RBAC · Phase C
          </p>
          <h1 className="mt-1 text-3xl font-bold text-[#0A192F] tracking-tight">
            Custom Roles &amp; Approval Workflows
          </h1>
          <p className="mt-2 text-sm text-slate-600 max-w-2xl">
            Define organisation-specific roles, gate sensitive actions
            behind multi-step approvals, and visualise the full role
            graph for your team.
          </p>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-6 border-b border-slate-200"
             data-testid="rbac-phase-c-tabs">
          {TABS.map((t) => {
            const Icon = t.icon;
            const isActive = active === t.key;
            return (
              <button
                key={t.key} type="button"
                onClick={() => setActive(t.key)}
                data-testid={`rbac-c-tab-${t.key}`}
                className={`relative px-4 py-2.5 text-xs font-bold uppercase tracking-wider inline-flex items-center gap-2 transition-all ${
                  isActive ? "text-[#0A192F]" : "text-slate-500 hover:text-[#0A192F]"
                }`}
              >
                <Icon className="h-3.5 w-3.5" />
                {t.label}
                {isActive && (
                  <span className="absolute -bottom-px left-0 right-0 h-0.5 bg-[#D4AF37]" />
                )}
              </button>
            );
          })}
        </div>

        {active === "custom-roles" && <CustomRolesTab />}
        {active === "chains" && <ApprovalChainsTab />}
        {active === "inbox" && <ApprovalInboxTab />}
        {active === "canvas" && <VisualCanvasTab />}
      </div>
    </DashboardLayout>
  );
}

// ===========================================================================
// 1. Custom Roles tab
// ===========================================================================
function CustomRolesTab() {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    key: "", label: "", description: "", color: "#0F4C81", base_role_key: "",
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/rbac/custom-roles");
      setRoles(r.data?.roles || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!form.key.trim() || !form.label.trim()) {
      toast.error("Role key and label are required.");
      return;
    }
    setCreating(true);
    try {
      await api.post("/rbac/custom-roles", {
        ...form,
        base_role_key: form.base_role_key || undefined,
      });
      toast.success(`Custom role '${form.key}' created.`);
      setForm({ key: "", label: "", description: "",
                color: "#0F4C81", base_role_key: "" });
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setCreating(false);
    }
  };

  const archive = async (id, key) => {
    if (!window.confirm(`Archive custom role '${key}'? This cannot be undone if assignments exist.`)) return;
    try {
      await api.delete(`/rbac/custom-roles/${id}`);
      toast.success("Role archived.");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div data-testid="rbac-c-custom-roles-tab">
      <div className="rounded-2xl border border-slate-200 bg-white p-5 mb-6"
           data-testid="custom-role-create-panel">
        <p className="text-xs uppercase tracking-[0.22em] text-slate-500 font-bold mb-3">
          New custom role
        </p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <input
            value={form.key}
            onChange={(e) => setForm({ ...form, key: e.target.value })}
            placeholder="role_key (lowercase_snake)"
            data-testid="custom-role-key-input"
            className="px-3 py-2 rounded-md border border-slate-200 text-sm font-mono"
          />
          <input
            value={form.label}
            onChange={(e) => setForm({ ...form, label: e.target.value })}
            placeholder="Display label"
            data-testid="custom-role-label-input"
            className="px-3 py-2 rounded-md border border-slate-200 text-sm"
          />
          <input
            value={form.base_role_key}
            onChange={(e) => setForm({ ...form, base_role_key: e.target.value })}
            placeholder="base_role_key (optional)"
            data-testid="custom-role-base-input"
            className="px-3 py-2 rounded-md border border-slate-200 text-sm font-mono"
          />
          <input
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            placeholder="Description"
            data-testid="custom-role-desc-input"
            className="px-3 py-2 rounded-md border border-slate-200 text-sm col-span-2"
          />
          <div className="flex items-center gap-2">
            <label className="text-xs text-slate-600">Color</label>
            <input
              type="color"
              value={form.color}
              onChange={(e) => setForm({ ...form, color: e.target.value })}
              data-testid="custom-role-color-input"
              className="h-9 w-12 rounded border border-slate-200"
            />
          </div>
        </div>
        <button onClick={create} disabled={creating}
                data-testid="custom-role-create-btn"
                className="mt-4 bg-[#0A192F] text-white text-xs font-bold uppercase tracking-wider px-4 py-2 rounded-md hover:bg-[#0F2C50] disabled:opacity-50 inline-flex items-center gap-2">
          <Plus className="h-3.5 w-3.5" />
          {creating ? "Creating…" : "Create custom role"}
        </button>
        <p className="mt-2 text-[11px] text-slate-500">
          Permissions can be edited after creation. Base role inheritance
          resolves at read time so upstream changes propagate automatically.
        </p>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white"
           data-testid="custom-roles-list">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
          <p className="text-xs uppercase tracking-[0.22em] text-slate-500 font-bold">
            Active custom roles · {roles.length}
          </p>
          <button onClick={load}
                  className="text-[11px] text-[#0F4C81] hover:underline inline-flex items-center gap-1">
            <RefreshCw className="h-3 w-3" /> Refresh
          </button>
        </div>
        {loading ? (
          <p className="px-5 py-6 text-sm text-slate-500">Loading…</p>
        ) : roles.length === 0 ? (
          <p className="px-5 py-6 text-sm text-slate-500">
            No custom roles yet. Use the form above to define your first one.
          </p>
        ) : (
          <div className="divide-y divide-slate-100">
            {roles.map((r) => (
              <div key={r.id}
                   data-testid={`custom-role-row-${r.id}`}
                   className="px-5 py-3 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="h-2.5 w-2.5 rounded-full"
                        style={{ backgroundColor: r.color }}></span>
                  <div>
                    <p className="text-sm font-semibold text-[#0A192F]">
                      {r.label}
                      <span className="ml-2 text-[10px] uppercase tracking-wider text-slate-400 font-mono">
                        {r.key}
                      </span>
                    </p>
                    <p className="text-[11px] text-slate-500">
                      {r.description || "No description"}
                      {r.base_role_key && (
                        <> · inherits from <span className="font-mono">{r.base_role_key}</span></>
                      )}
                      {" · "}
                      {Object.keys(r.permissions || {}).length} permission{Object.keys(r.permissions || {}).length === 1 ? "" : "s"}
                    </p>
                  </div>
                </div>
                <button onClick={() => archive(r.id, r.key)}
                        data-testid={`custom-role-archive-${r.id}`}
                        className="text-slate-400 hover:text-rose-600">
                  <Archive className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ===========================================================================
// 2. Approval Chains tab
// ===========================================================================
function ApprovalChainsTab() {
  const [chains, setChains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    name: "", action_kind: "",
    steps: [{ step_number: 1, required_approver_roles: "org_admin",
              min_approvers: 1 }],
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/rbac/approval-chains");
      setChains(r.data?.chains || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const addStep = () => {
    if (form.steps.length >= 5) {
      toast.error("Approval chains are capped at 5 steps.");
      return;
    }
    setForm({
      ...form,
      steps: [...form.steps, {
        step_number: form.steps.length + 1,
        required_approver_roles: "org_admin", min_approvers: 1,
      }],
    });
  };

  const removeStep = (idx) => {
    const next = form.steps.filter((_, i) => i !== idx);
    next.forEach((s, i) => { s.step_number = i + 1; });
    setForm({ ...form, steps: next });
  };

  const create = async () => {
    if (!form.name.trim() || !form.action_kind.trim()) {
      toast.error("Name and action_kind are required.");
      return;
    }
    setCreating(true);
    try {
      const steps = form.steps.map((s) => ({
        step_number: Number(s.step_number),
        required_approver_roles: s.required_approver_roles.split(",")
          .map((x) => x.trim()).filter(Boolean),
        min_approvers: Number(s.min_approvers),
      }));
      await api.post("/rbac/approval-chains",
        { name: form.name, action_kind: form.action_kind, steps });
      toast.success(`Chain '${form.name}' created.`);
      setForm({
        name: "", action_kind: "",
        steps: [{ step_number: 1, required_approver_roles: "org_admin", min_approvers: 1 }],
      });
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setCreating(false);
    }
  };

  const archive = async (id) => {
    if (!window.confirm("Archive this approval chain?")) return;
    try {
      await api.delete(`/rbac/approval-chains/${id}`);
      toast.success("Chain archived.");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div data-testid="rbac-c-chains-tab">
      <div className="rounded-2xl border border-slate-200 bg-white p-5 mb-6"
           data-testid="chain-create-panel">
        <p className="text-xs uppercase tracking-[0.22em] text-slate-500 font-bold mb-3">
          New approval chain
        </p>
        <div className="grid sm:grid-cols-2 gap-3 mb-4">
          <input
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="Chain name (e.g. Super Chair Approval)"
            data-testid="chain-name-input"
            className="px-3 py-2 rounded-md border border-slate-200 text-sm"
          />
          <input
            value={form.action_kind}
            onChange={(e) => setForm({ ...form, action_kind: e.target.value })}
            placeholder="action_kind (e.g. assign_super_chair)"
            data-testid="chain-action-input"
            className="px-3 py-2 rounded-md border border-slate-200 text-sm font-mono"
          />
        </div>
        <div className="space-y-3" data-testid="chain-steps-list">
          {form.steps.map((s, i) => (
            <div key={i} className="grid sm:grid-cols-12 gap-2 items-center"
                 data-testid={`chain-step-row-${i}`}>
              <div className="sm:col-span-1 text-xs font-bold text-slate-500">
                #{s.step_number}
              </div>
              <input
                value={s.required_approver_roles}
                onChange={(e) => {
                  const next = [...form.steps];
                  next[i].required_approver_roles = e.target.value;
                  setForm({ ...form, steps: next });
                }}
                placeholder="approver roles (comma-separated)"
                data-testid={`chain-step-roles-${i}`}
                className="sm:col-span-7 px-3 py-2 rounded-md border border-slate-200 text-sm font-mono"
              />
              <input
                type="number" min="1" max="10"
                value={s.min_approvers}
                onChange={(e) => {
                  const next = [...form.steps];
                  next[i].min_approvers = e.target.value;
                  setForm({ ...form, steps: next });
                }}
                placeholder="min"
                data-testid={`chain-step-min-${i}`}
                className="sm:col-span-2 px-3 py-2 rounded-md border border-slate-200 text-sm"
              />
              <div className="sm:col-span-2">
                {form.steps.length > 1 && (
                  <button onClick={() => removeStep(i)}
                          data-testid={`chain-step-remove-${i}`}
                          className="text-rose-600 text-xs hover:underline">
                    Remove
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          <button onClick={addStep}
                  data-testid="chain-add-step-btn"
                  className="text-xs bg-white border border-slate-200 px-3 py-1.5 rounded-md hover:border-slate-300 inline-flex items-center gap-1">
            <Plus className="h-3 w-3" /> Add step
          </button>
          <button onClick={create} disabled={creating}
                  data-testid="chain-create-btn"
                  className="bg-[#0A192F] text-white text-xs font-bold uppercase tracking-wider px-4 py-2 rounded-md hover:bg-[#0F2C50] disabled:opacity-50 inline-flex items-center gap-2">
            <Workflow className="h-3.5 w-3.5" />
            {creating ? "Creating…" : "Create chain"}
          </button>
        </div>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white"
           data-testid="chains-list">
        <p className="px-5 py-4 text-xs uppercase tracking-[0.22em] text-slate-500 font-bold border-b border-slate-200">
          Active chains · {chains.length}
        </p>
        {loading ? (
          <p className="px-5 py-6 text-sm text-slate-500">Loading…</p>
        ) : chains.length === 0 ? (
          <p className="px-5 py-6 text-sm text-slate-500">
            No approval chains yet. Define one above to start gating sensitive actions.
          </p>
        ) : (
          <div className="divide-y divide-slate-100">
            {chains.map((c) => (
              <div key={c.id}
                   data-testid={`chain-row-${c.id}`}
                   className="px-5 py-3 flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold text-[#0A192F]">
                    {c.name}
                    <span className="ml-2 text-[10px] uppercase tracking-wider text-slate-400 font-mono">
                      {c.action_kind}
                    </span>
                  </p>
                  <p className="text-[11px] text-slate-500">
                    {c.steps?.length || 0} step{c.steps?.length === 1 ? "" : "s"} ·
                    {" "}created {new Date(c.created_at).toLocaleDateString()}
                  </p>
                </div>
                <button onClick={() => archive(c.id)}
                        data-testid={`chain-archive-${c.id}`}
                        className="text-slate-400 hover:text-rose-600">
                  <Archive className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ===========================================================================
// 3. Approval Inbox tab
// ===========================================================================
function ApprovalInboxTab() {
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/rbac/approval-requests/inbox");
      setPending(r.data?.requests || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const approve = async (id) => {
    try {
      const r = await api.post(`/rbac/approval-requests/${id}/approve?comment=approved+from+ui`);
      toast.success(`Request ${r.data?.status === "approved" ? "fully approved" : "advanced to next step"}.`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const reject = async (id) => {
    const reason = window.prompt("Reason for rejecting:") || "";
    try {
      await api.post(`/rbac/approval-requests/${id}/reject?reason=${encodeURIComponent(reason)}`);
      toast.success("Request rejected.");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div data-testid="rbac-c-inbox-tab">
      <div className="rounded-2xl border border-slate-200 bg-white"
           data-testid="approval-inbox-list">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
          <p className="text-xs uppercase tracking-[0.22em] text-slate-500 font-bold">
            Pending for me · {pending.length}
          </p>
          <button onClick={load}
                  className="text-[11px] text-[#0F4C81] hover:underline inline-flex items-center gap-1">
            <RefreshCw className="h-3 w-3" /> Refresh
          </button>
        </div>
        {loading ? (
          <p className="px-5 py-6 text-sm text-slate-500">Loading…</p>
        ) : pending.length === 0 ? (
          <p className="px-5 py-6 text-sm text-slate-500"
             data-testid="approval-inbox-empty">
            Inbox zero. No pending approvals require your action.
          </p>
        ) : (
          <div className="divide-y divide-slate-100">
            {pending.map((req) => (
              <div key={req.id}
                   data-testid={`approval-row-${req.id}`}
                   className="px-5 py-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-[#0A192F]">
                      {req.chain_name}
                      <span className="ml-2 text-[10px] uppercase tracking-wider text-slate-400 font-mono">
                        {req.action_kind}
                      </span>
                    </p>
                    <p className="text-[11px] text-slate-500">
                      Requested by {req.requested_by_email || req.requested_by} ·
                      {" "}step {req.current_step} · expires{" "}
                      {req.expires_at ? new Date(req.expires_at).toLocaleString() : "never"}
                    </p>
                    {Object.keys(req.target_payload || {}).length > 0 && (
                      <details className="mt-2">
                        <summary className="text-[11px] text-[#0F4C81] cursor-pointer">
                          View payload
                        </summary>
                        <pre className="mt-1 p-2 bg-slate-50 rounded text-[10px] font-mono overflow-x-auto">
{JSON.stringify(req.target_payload, null, 2)}
                        </pre>
                      </details>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={() => approve(req.id)}
                            data-testid={`approval-approve-${req.id}`}
                            className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-bold rounded-md bg-emerald-600 text-white hover:bg-emerald-700">
                      <Check className="h-3 w-3" /> Approve
                    </button>
                    <button onClick={() => reject(req.id)}
                            data-testid={`approval-reject-${req.id}`}
                            className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-bold rounded-md bg-white border border-slate-200 hover:border-rose-300 text-rose-600">
                      <X className="h-3 w-3" /> Reject
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ===========================================================================
// 4. Visual Canvas tab
// ===========================================================================
function VisualCanvasTab() {
  const [graph, setGraph] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeNode, setActiveNode] = useState(null);
  const [filter, setFilter] = useState("all");  // all | system | custom
  const containerRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/rbac/canvas");
      setGraph(r.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Compute simple radial layout — categorise nodes then place them in
  // concentric rings.  No external graph library needed.
  const layout = useMemo(() => {
    if (!graph) return { nodes: [], edges: [] };
    const filtered = filter === "all"
      ? graph.nodes
      : graph.nodes.filter((n) => n.kind === filter);
    const categories = {};
    filtered.forEach((n) => {
      const cat = n.category || "other";
      if (!categories[cat]) categories[cat] = [];
      categories[cat].push(n);
    });
    const catKeys = Object.keys(categories).sort();
    const centerX = 500;
    const centerY = 350;
    const ringStep = 110;
    const placed = {};
    catKeys.forEach((cat, ci) => {
      const ring = ci + 1;
      const radius = ringStep * ring;
      const items = categories[cat];
      items.forEach((n, idx) => {
        const angle = (idx / items.length) * Math.PI * 2 + (ci * 0.3);
        placed[n.id] = {
          ...n,
          x: centerX + Math.cos(angle) * radius,
          y: centerY + Math.sin(angle) * radius,
          ring,
        };
      });
    });
    const placedEdges = graph.edges
      .map((e) => ({
        ...e,
        src: placed[e.source], tgt: placed[e.target],
      }))
      .filter((e) => e.src && e.tgt);
    return { nodes: Object.values(placed), edges: placedEdges };
  }, [graph, filter]);

  return (
    <div data-testid="rbac-c-canvas-tab">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="rounded-md border border-slate-200 bg-white p-1 inline-flex">
          {["all", "system", "custom"].map((k) => (
            <button key={k}
                    onClick={() => setFilter(k)}
                    data-testid={`canvas-filter-${k}`}
                    className={`px-3 py-1 text-[11px] uppercase tracking-wider font-bold rounded ${
                      filter === k
                        ? "bg-[#0A192F] text-white"
                        : "text-slate-500 hover:text-[#0A192F]"
                    }`}>
              {k}
            </button>
          ))}
        </div>
        <p className="text-[11px] text-slate-500">
          {graph?.nodes?.length || 0} nodes · {graph?.edges?.length || 0} edges
          {graph?.custom_role_count > 0 && ` · ${graph.custom_role_count} custom roles`}
          {graph?.chain_count > 0 && ` · ${graph.chain_count} chains`}
        </p>
        <button onClick={load}
                className="ml-auto text-[11px] text-[#0F4C81] hover:underline inline-flex items-center gap-1">
          <RefreshCw className="h-3 w-3" /> Refresh
        </button>
      </div>

      <div ref={containerRef}
           className="rounded-2xl border border-slate-200 bg-gradient-to-br from-slate-50 to-white overflow-hidden"
           data-testid="canvas-svg-wrapper">
        {loading ? (
          <p className="p-6 text-sm text-slate-500">Loading graph…</p>
        ) : (
          <svg viewBox="0 0 1000 700" className="w-full" style={{ height: 700 }}
               data-testid="canvas-svg">
            <defs>
              <marker id="arrow-inherits" viewBox="0 0 10 10" refX="9" refY="5"
                      markerWidth="6" markerHeight="6" orient="auto-start-reverse">
                <path d="M 0 0 L 10 5 L 0 10 z" fill="#94a3b8" />
              </marker>
              <marker id="arrow-approves" viewBox="0 0 10 10" refX="9" refY="5"
                      markerWidth="6" markerHeight="6" orient="auto-start-reverse">
                <path d="M 0 0 L 10 5 L 0 10 z" fill="#D4AF37" />
              </marker>
            </defs>
            {/* Edges first so nodes render on top */}
            {layout.edges.map((e, i) => {
              const color = e.kind === "approves" ? "#D4AF37" : "#cbd5e1";
              const marker = e.kind === "approves"
                ? "url(#arrow-approves)" : "url(#arrow-inherits)";
              const dash = e.kind === "approves" ? "4 4" : "";
              return (
                <line key={i}
                      x1={e.src.x} y1={e.src.y}
                      x2={e.tgt.x} y2={e.tgt.y}
                      stroke={color} strokeWidth="1.5"
                      strokeDasharray={dash}
                      markerEnd={marker}
                      opacity="0.65" />
              );
            })}
            {layout.nodes.map((n) => {
              const isActive = activeNode?.id === n.id;
              const r = isActive ? 22 : 18;
              const fill = n.kind === "custom"
                ? (n.color || "#D4AF37")
                : "#0F4C81";
              return (
                <g key={n.id}
                   data-testid={`canvas-node-${n.id}`}
                   transform={`translate(${n.x}, ${n.y})`}
                   onClick={() => setActiveNode(n)}
                   style={{ cursor: "pointer" }}>
                  <circle r={r + 4}
                          fill="white" stroke={fill}
                          strokeWidth={isActive ? 3 : 1.5} />
                  <circle r={r} fill={fill} opacity="0.85" />
                  <text textAnchor="middle" y={r + 14}
                        fontSize="9" fontWeight="600" fill="#0A192F">
                    {n.label.length > 18 ? n.label.slice(0, 16) + "…" : n.label}
                  </text>
                </g>
              );
            })}
          </svg>
        )}
      </div>

      {/* Active node side-card */}
      {activeNode && (
        <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-5"
             data-testid="canvas-active-node">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-[10px] uppercase tracking-[0.22em] text-slate-400 font-bold">
                {activeNode.kind} · {activeNode.category}
              </p>
              <p className="mt-1 text-lg font-bold text-[#0A192F]">
                {activeNode.label}
              </p>
              <p className="text-xs font-mono text-slate-500">{activeNode.id}</p>
              {activeNode.kind === "custom" && (
                <>
                  {activeNode.base_role_key && (
                    <p className="mt-1 text-xs text-slate-600">
                      Inherits from{" "}
                      <span className="font-mono">{activeNode.base_role_key}</span>
                    </p>
                  )}
                  <p className="mt-1 text-xs text-slate-600">
                    {activeNode.permission_count} explicit permission
                    {activeNode.permission_count === 1 ? "" : "s"}
                  </p>
                </>
              )}
            </div>
            <button onClick={() => setActiveNode(null)}
                    data-testid="canvas-active-close"
                    className="text-slate-400 hover:text-rose-600">
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* Legend */}
      <div className="mt-4 flex flex-wrap items-center gap-4 text-[11px] text-slate-600">
        <div className="flex items-center gap-2">
          <span className="h-3 w-3 rounded-full bg-[#0F4C81]"></span>
          System roles
        </div>
        <div className="flex items-center gap-2">
          <span className="h-3 w-3 rounded-full bg-[#D4AF37]"></span>
          Custom roles
        </div>
        <div className="flex items-center gap-2">
          <span className="inline-block w-6 border-t border-slate-400"></span>
          Inherits
        </div>
        <div className="flex items-center gap-2">
          <span className="inline-block w-6 border-t border-dashed border-[#D4AF37]"></span>
          Approves
        </div>
      </div>
    </div>
  );
}
